import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { DsAccordionComponent } from '../containment/ds-accordion.component';
import { DsPaginatorComponent } from '../navigation/ds-paginator.component';
import { DsCheckComponent } from '../selection/ds-check.component';

export type DsTableKey = string | number;

export interface DsTableColumn<T> {
  key: string;
  header: string;
  value: (row: T) => string;
  width?: string;
}

export interface DsTableCellContext<T> {
  $implicit: T;
  column: DsTableColumn<T>;
}

/** Custom cell renderer associated with a column key. */
@Directive({
  selector: 'ng-template[dsTableCell]',
  standalone: true,
})
export class DsTableCellDefDirective<T = unknown> {
  @Input('dsTableCell') key = '';

  constructor(readonly template: TemplateRef<DsTableCellContext<T>>) {}

  static ngTemplateContextGuard<T>(
    _directive: DsTableCellDefDirective<T>,
    _context: unknown,
  ): _context is DsTableCellContext<T> {
    return true;
  }
}

@Component({
  selector: 'th[ds-header-cell]',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '<ng-content />',
  styles: [`
    :host {
      height: var(--ds-table-row-height);
      padding: var(--ds-table-cell-padding-y) var(--ds-table-cell-padding-x);
      box-sizing: border-box;
      color: var(--ds-table-header-text-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-table-header-font-size);
      font-weight: var(--ds-table-header-font-weight);
      line-height: var(--ds-table-header-line-height);
      text-align: left;
      vertical-align: middle;
    }
  `],
})
export class DsHeaderCellComponent {}

@Component({
  selector: 'td[ds-cell]',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '<ng-content />',
  styles: [`
    :host {
      height: var(--ds-table-row-height);
      padding: var(--ds-table-cell-padding-y) var(--ds-table-cell-padding-x);
      box-sizing: border-box;
      color: var(--ds-table-cell-text-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-table-cell-font-size);
      font-weight: var(--ds-table-cell-font-weight);
      line-height: var(--ds-table-cell-line-height);
      text-align: left;
      vertical-align: middle;
    }
  `],
})
export class DsCellComponent {}

/**
 * Paginated data table that can opt into the compact accordion representation.
 * `page` and `pageChange` are zero-based; the visible labels remain one-based.
 */
@Component({
  selector: 'ds-table',
  standalone: true,
  imports: [
    CommonModule,
    DsAccordionComponent,
    DsCellComponent,
    DsCheckComponent,
    DsHeaderCellComponent,
    DsPaginatorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ds-table">
      @if (useCompactLayout) {
        <div class="ds-table__compact" role="list">
          @for (row of visibleRows; track rowIdentity(row, $index)) {
            <div class="ds-table__compact-row" role="listitem">
              <ds-accordion
                class="ds-table__accordion"
                [class.ds-table__accordion--expanded]="isExpanded(row, $index)"
                shadow="none"
                [borderColor]="'var(--ds-table-border-color)'"
                [expanded]="isExpanded(row, $index)"
                (expandedChange)="setExpanded(row, $index, $event)"
              >
                <span
                  ds-accordion-header
                  class="ds-table__compact-pair ds-table__compact-header"
                  [class.ds-table__compact-header--selectable]="selectable"
                >
                  <strong class="ds-table__compact-label">{{ columns[0].header }}</strong>
                  <span class="ds-table__compact-value">
                    @if (cellTemplate(columns[0].key); as customCell) {
                      <ng-container
                        [ngTemplateOutlet]="customCell"
                        [ngTemplateOutletContext]="cellContext(row, columns[0])"
                      />
                    } @else {
                      {{ columns[0].value(row) }}
                    }
                  </span>
                </span>

                <div ds-accordion-content>
                  @for (column of compactColumns; track column.key) {
                    <div class="ds-table__compact-pair">
                      <strong class="ds-table__compact-label">{{ column.header }}</strong>
                      <span class="ds-table__compact-value">
                        @if (cellTemplate(column.key); as customCell) {
                          <ng-container
                            [ngTemplateOutlet]="customCell"
                            [ngTemplateOutletContext]="cellContext(row, column)"
                          />
                        } @else {
                          {{ column.value(row) }}
                        }
                      </span>
                    </div>
                  }
                </div>
              </ds-accordion>

              @if (selectable) {
                <ds-check
                  class="ds-table__compact-check"
                  [checked]="isSelected(row, $index)"
                  [ariaLabel]="isSelected(row, $index) ? 'Deseleccionar fila' : 'Seleccionar fila'"
                  (checkedChange)="setSelected(row, $index, $event)"
                />
              }
            </div>
          }
        </div>
      } @else {
        <div class="ds-table__frame">
          <table>
            <thead>
              <tr>
                @if (selectable) {
                  <th ds-header-cell class="ds-table__selection-cell">
                    <ds-check
                      [checked]="allVisibleSelected"
                      [ariaLabel]="allVisibleSelected ? 'Deseleccionar filas visibles' : 'Seleccionar filas visibles'"
                      (checkedChange)="setVisibleSelected($event)"
                    />
                  </th>
                }
                @for (column of columns; track column.key) {
                  <th ds-header-cell scope="col" [style.width]="column.width || null">
                    {{ column.header }}
                  </th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of visibleRows; track rowIdentity(row, $index)) {
                <tr>
                  @if (selectable) {
                    <td ds-cell class="ds-table__selection-cell">
                      <ds-check
                        [checked]="isSelected(row, $index)"
                        [ariaLabel]="isSelected(row, $index) ? 'Deseleccionar fila' : 'Seleccionar fila'"
                        (checkedChange)="setSelected(row, $index, $event)"
                      />
                    </td>
                  }
                  @for (column of columns; track column.key) {
                    <td ds-cell>
                      @if (cellTemplate(column.key); as customCell) {
                        <ng-container
                          [ngTemplateOutlet]="customCell"
                          [ngTemplateOutletContext]="cellContext(row, column)"
                        />
                      } @else {
                        {{ column.value(row) }}
                      }
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      @if (pageCount > 1) {
        <ds-paginator
          class="ds-table__pagination"
          [pageCount]="pageCount"
          [page]="safePage"
          ariaLabel="Paginación de la tabla"
          (pageChange)="setPage($event)"
        />
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .ds-table {
      display: flex;
      width: 100%;
      flex-direction: column;
      gap: var(--ds-table-content-gap);
    }

    .ds-table__frame {
      width: 100%;
      overflow: hidden;
      border: var(--ds-table-border-width) solid var(--ds-table-border-color);
      border-radius: var(--ds-table-border-radius);
      background: var(--ds-table-background);
      box-sizing: border-box;
    }

    table {
      width: 100%;
      border-spacing: 0;
      table-layout: fixed;
    }

    thead {
      background: var(--ds-table-header-background);
    }

    thead th,
    tbody tr:not(:last-child) td {
      border-bottom: var(--ds-table-border-width) solid var(--ds-table-border-color);
    }

    tr {
      display: table-row;
    }

    .ds-table__selection-cell {
      width: var(--ds-table-selection-column-width);
      padding: 0;
      text-align: center;
    }

    .ds-table__selection-cell ds-check {
      vertical-align: middle;
    }

    .ds-table__compact {
      display: flex;
      width: 100%;
      flex-direction: column;
      gap: var(--ds-table-mobile-row-gap);
    }

    .ds-table__compact-row {
      position: relative;
      width: 100%;
    }

    .ds-table__accordion {
      --ds-accordion-background: var(--ds-table-background);
      --ds-accordion-header-background: var(--ds-table-accordion-collapsed-background);
      --ds-accordion-divider-color: var(--ds-table-border-color);
      --ds-accordion-title-color: var(--ds-table-header-text-color);
      --ds-accordion-content-color: var(--ds-table-cell-text-color);
      --ds-accordion-border-radius: var(--ds-table-border-radius);
      --ds-accordion-divider-width: var(--ds-table-border-width);
      --ds-accordion-title-font-size: var(--ds-table-header-font-size);
      --ds-accordion-title-line-height: var(--ds-table-header-line-height);
      --ds-accordion-title-font-weight: var(--ds-table-header-font-weight);
      --ds-accordion-content-gap: var(--ds-table-mobile-content-gap);
    }

    .ds-table__accordion--expanded {
      --ds-accordion-header-background: var(--ds-table-header-background);
    }

    .ds-table__compact-pair {
      display: flex;
      width: 100%;
      min-width: 0;
      align-items: center;
      justify-content: space-between;
      gap: var(--ds-table-mobile-content-gap);
      color: var(--ds-table-cell-text-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-table-cell-font-size);
      font-weight: var(--ds-table-cell-font-weight);
      line-height: var(--ds-table-cell-line-height);
    }

    .ds-table__compact-header {
      min-height: var(--ds-table-mobile-header-content-min-height);
    }

    .ds-table__compact-header--selectable {
      padding-left: var(--ds-table-mobile-selection-inset);
    }

    .ds-table__compact-label {
      color: var(--ds-table-header-text-color);
      font-weight: var(--ds-table-header-font-weight);
    }

    .ds-table__compact-value {
      min-width: 0;
      text-align: right;
    }

    .ds-table__compact-check {
      position: absolute;
      z-index: var(--ds-table-mobile-check-z-index);
      top: var(--ds-table-mobile-check-inset-y);
      left: var(--ds-table-mobile-check-inset-x);
    }

    .ds-table__pagination {
      align-self: flex-end;
    }
  `],
})
export class DsTableComponent<T = unknown> implements AfterViewInit, OnChanges, OnDestroy {
  @Input() columns: readonly DsTableColumn<T>[] = [];
  @Input() rows: readonly T[] = [];
  @Input() rowKey: (row: T, index: number) => DsTableKey = (_row, index) => index;
  @Input() itemsPerPage = 10;
  @Input() page = 0;
  @Input() selectable = false;
  @Input() selectedKeys: readonly DsTableKey[] = [];
  @Input() responsive = false;
  @Input() expandedByDefault = false;
  @Input() allowMultipleExpanded = false;

  @Output() readonly pageChange = new EventEmitter<number>();
  @Output() readonly selectedKeysChange = new EventEmitter<readonly DsTableKey[]>();

  @ContentChildren(DsTableCellDefDirective)
  private cellDefs?: QueryList<DsTableCellDefDirective<T>>;

  private readonly expandedKeys = new Set<DsTableKey>();
  private resizeObserver?: ResizeObserver;
  private compactLayout = false;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? this.host.nativeElement.clientWidth;
      this.zone.run(() => this.updateCompactLayout(width));
    });
    this.resizeObserver.observe(this.host.nativeElement);
    this.updateCompactLayout(this.host.nativeElement.clientWidth);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.itemsPerPage < 1) this.itemsPerPage = 1;
    this.page = this.safePage;

    if (
      changes['rows'] ||
      changes['rowKey'] ||
      changes['expandedByDefault'] ||
      changes['allowMultipleExpanded']
    ) {
      if (changes['expandedByDefault']) this.expandedKeys.clear();
      this.syncExpandedKeys();
    }
    if (changes['responsive']) {
      this.updateCompactLayout(this.host.nativeElement.clientWidth);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  get useCompactLayout(): boolean {
    return this.responsive && this.compactLayout && this.columns.length > 0;
  }

  get pageCount(): number {
    return this.rows.length === 0 ? 0 : Math.ceil(this.rows.length / this.itemsPerPage);
  }

  get safePage(): number {
    return Math.min(Math.max(this.page, 0), Math.max(this.pageCount - 1, 0));
  }

  get visibleRows(): readonly T[] {
    const start = this.safePage * this.itemsPerPage;
    return this.rows.slice(start, start + this.itemsPerPage);
  }

  get compactColumns(): readonly DsTableColumn<T>[] {
    return this.columns.slice(1);
  }

  get allVisibleSelected(): boolean {
    const selected = new Set(this.selectedKeys);
    const keys = this.visibleRows.map((row, index) => this.keyFor(row, index));
    return keys.length > 0 && keys.every(key => selected.has(key));
  }

  rowIdentity(row: T, visibleIndex: number): DsTableKey {
    return this.keyFor(row, visibleIndex);
  }

  isSelected(row: T, visibleIndex: number): boolean {
    return new Set(this.selectedKeys).has(this.keyFor(row, visibleIndex));
  }

  setSelected(row: T, visibleIndex: number, checked: boolean): void {
    const next = new Set(this.selectedKeys);
    const key = this.keyFor(row, visibleIndex);
    checked ? next.add(key) : next.delete(key);
    this.selectedKeys = [...next];
    this.selectedKeysChange.emit(this.selectedKeys);
  }

  setVisibleSelected(checked: boolean): void {
    const next = new Set(this.selectedKeys);
    this.visibleRows.forEach((row, index) => {
      const key = this.keyFor(row, index);
      checked ? next.add(key) : next.delete(key);
    });
    this.selectedKeys = [...next];
    this.selectedKeysChange.emit(this.selectedKeys);
  }

  isExpanded(row: T, visibleIndex: number): boolean {
    return this.expandedKeys.has(this.keyFor(row, visibleIndex));
  }

  setExpanded(row: T, visibleIndex: number, expanded: boolean): void {
    const key = this.keyFor(row, visibleIndex);
    if (expanded && !this.allowMultipleExpanded) this.expandedKeys.clear();
    expanded ? this.expandedKeys.add(key) : this.expandedKeys.delete(key);
    this.cdr.markForCheck();
  }

  setPage(nextPage: number): void {
    const safeNextPage = Math.min(Math.max(nextPage, 0), Math.max(this.pageCount - 1, 0));
    if (safeNextPage === this.safePage) return;
    this.page = safeNextPage;
    this.pageChange.emit(safeNextPage);
    this.cdr.markForCheck();
  }

  cellTemplate(key: string): TemplateRef<DsTableCellContext<T>> | null {
    return this.cellDefs?.find(definition => definition.key === key)?.template ?? null;
  }

  cellContext(row: T, column: DsTableColumn<T>): DsTableCellContext<T> {
    return { $implicit: row, column };
  }

  private keyFor(row: T, visibleIndex: number): DsTableKey {
    return this.rowKey(row, this.safePage * this.itemsPerPage + visibleIndex);
  }

  private syncExpandedKeys(): void {
    const validKeys = new Set(this.rows.map((row, index) => this.rowKey(row, index)));
    for (const key of this.expandedKeys) {
      if (!validKeys.has(key)) this.expandedKeys.delete(key);
    }
    if (this.expandedByDefault) {
      if (this.allowMultipleExpanded) {
        validKeys.forEach(key => this.expandedKeys.add(key));
      } else if (this.expandedKeys.size === 0) {
        const firstKey = validKeys.values().next().value as DsTableKey | undefined;
        if (firstKey !== undefined) this.expandedKeys.add(firstKey);
      }
    }
    if (!this.allowMultipleExpanded && this.expandedKeys.size > 1) {
      const firstExpanded = this.expandedKeys.values().next().value as DsTableKey;
      this.expandedKeys.clear();
      this.expandedKeys.add(firstExpanded);
    }
  }

  private updateCompactLayout(width: number): void {
    if (typeof getComputedStyle === 'undefined') return;
    const rawBreakpoint = getComputedStyle(this.host.nativeElement)
      .getPropertyValue('--ds-table-responsive-breakpoint');
    const breakpoint = Number.parseFloat(rawBreakpoint);
    const next = Number.isFinite(breakpoint) && width < breakpoint;
    if (next === this.compactLayout) return;
    this.compactLayout = next;
    this.cdr.markForCheck();
  }
}
